/**
 * Master Profile Page - Complete Redesign
 * Unified design with Admin TMA (Slate-50 bg, Blue-700 header)
 * Internal routing with useState
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  Home,
  ClipboardList,
  Clock,
  User,
  Star,
  Shield,
  Wrench,
  Phone,
  ChevronRight,
  CheckCircle,
  Video,
  Settings,
} from 'lucide-react';
import {
  fetchUserProfile,
  fetchUserOrders,
  fetchReviews,
  updateMasterStatus,
  type MasterProfile,
  type UserOrder,
} from '@/lib/userApi';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const BOT_USERNAME = 'gasworkerttest_bot';

type TabId = 'dashboard' | 'orders' | 'history' | 'profile';

// Utility functions
function formatPrice(price: string | null | undefined): string {
  if (!price) return '0 ₽';
  const num = Number(price);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} ч назад`;
  return formatDate(dateString);
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  BIDDING: 'Торги',
  ASSIGNED: 'Назначен',
  IN_PROGRESS: 'В работе',
  PAYMENT_PENDING: 'Ожидание оплаты',
  PAID_CARD: 'Оплачен',
  PAID_CASH: 'Оплачен',
  COMPLETED: 'Завершён',
  CANCELED: 'Отменён',
  REFUNDED: 'Возврат',
  DISPUTED: 'Спор',
};

const ORDER_STATUS_COLORS: Record<string, string> = {
  BIDDING: 'bg-blue-100 text-blue-700',
  ASSIGNED: 'bg-purple-100 text-purple-700',
  IN_PROGRESS: 'bg-orange-100 text-orange-700',
  PAYMENT_PENDING: 'bg-yellow-100 text-yellow-700',
  PAID_CARD: 'bg-green-100 text-green-700',
  PAID_CASH: 'bg-green-100 text-green-700',
  COMPLETED: 'bg-slate-100 text-slate-700',
  CANCELED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-amber-100 text-amber-700',
  DISPUTED: 'bg-red-100 text-red-700',
};

const ORDER_TYPE_ICONS: Record<string, React.ReactNode> = {
  VIDEO_CONSULT: <Video className="h-5 w-5" />,
  REPAIR: <Wrench className="h-5 w-5" />,
  MAINTENANCE: <Settings className="h-5 w-5" />,
};

// Navigation items
const navItems: { id: TabId; icon: React.ElementType; label: string }[] = [
  { id: 'dashboard', icon: Home, label: 'Главная' },
  { id: 'orders', icon: ClipboardList, label: 'Заказы' },
  { id: 'history', icon: Clock, label: 'История' },
  { id: 'profile', icon: User, label: 'Профиль' },
];

export function MasterProfilePage() {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');
  const queryClient = useQueryClient();

  // Fetch profile
  const { data: profile, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: ['userProfile'],
    queryFn: fetchUserProfile,
  });

  // Fetch active orders (for dashboard)
  const { data: activeOrdersData } = useQuery({
    queryKey: ['masterActiveOrders'],
    queryFn: () => fetchUserOrders({ limit: 5 }),
    enabled: !!profile && profile.role === 'MASTER',
  });

  // Fetch all orders
  const { data: allOrdersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['masterAllOrders'],
    queryFn: () => fetchUserOrders({ limit: 20 }),
    enabled: !!profile && profile.role === 'MASTER' && activeTab === 'orders',
  });

  // Fetch completed orders (history)
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['masterHistory'],
    queryFn: () => fetchUserOrders({ status: 'COMPLETED', limit: 20 }),
    enabled: !!profile && profile.role === 'MASTER' && activeTab === 'history',
  });

  // Fetch reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['masterReviews'],
    queryFn: () => fetchReviews({ limit: 10 }),
    enabled: !!profile && profile.role === 'MASTER' && activeTab === 'profile',
  });

  // Toggle status mutation
  const statusMutation = useMutation({
    mutationFn: updateMasterStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
    },
    onError: () => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
    },
  });

  // Loading state
  if (profileLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Загрузка...</div>
      </div>
    );
  }

  // Error state
  if (profileError || !profile) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 p-4 text-center bg-slate-50">
        <div className="text-red-600 font-bold text-lg">Ошибка загрузки</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-slate-200 rounded-xl text-sm"
        >
          Повторить
        </button>
      </div>
    );
  }

  // Access check
  if (profile.role !== 'MASTER') {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-slate-500">Доступ только для мастеров</div>
      </div>
    );
  }

  const masterProfile = profile as MasterProfile;
  const isOnline = masterProfile.masterStatus === 'ONLINE';
  const qrUrl = `https://t.me/${BOT_USERNAME}?start=verify_${masterProfile.id}`;

  // Find active order (IN_PROGRESS)
  const activeOrder = activeOrdersData?.data.find(
    (o) => o.status === 'IN_PROGRESS' || o.status === 'ASSIGNED'
  );

  // Calculate today's earnings
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEarnings = activeOrdersData?.data
    .filter((o) => o.status === 'COMPLETED' && new Date(o.completedAt || '') >= todayStart)
    .reduce((sum, o) => sum + Number(o.finalPrice || 0), 0) || 0;

  const handleToggleStatus = () => {
    const newStatus = isOnline ? 'OFFLINE' : 'ONLINE';
    statusMutation.mutate(newStatus);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
  };

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Header - Blue-700 style like Admin TMA */}
      <header className="sticky top-0 z-50 safe-area-top">
        <div className="bg-blue-700/95 backdrop-blur-md shadow-md">
          <div className="px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-3">
              {masterProfile.photoUrl ? (
                <img
                  src={masterProfile.photoUrl}
                  alt={masterProfile.name}
                  className="w-10 h-10 rounded-full border-2 border-white/30 object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
                  {masterProfile.name.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-white font-bold text-sm leading-tight">{masterProfile.name}</h1>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                  <span className="text-blue-100 text-xs font-medium">
                    {masterProfile.rating?.toFixed(1) || '0.0'}
                  </span>
                </div>
              </div>
            </div>

            {/* Online Toggle */}
            <button
              onClick={handleToggleStatus}
              disabled={statusMutation.isPending}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all',
                isOnline ? 'bg-green-500/30' : 'bg-black/20'
              )}
            >
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  isOnline ? 'bg-green-400 status-online' : 'bg-slate-400'
                )}
              />
              <span className="text-[10px] font-bold text-white">
                {isOnline ? 'В сети' : 'Оффлайн'}
              </span>
              <div
                className={cn(
                  'toggle-switch',
                  isOnline ? 'bg-green-500 active' : 'bg-slate-400'
                )}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto px-4 py-4 pb-24">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-4">
            {/* QR Card - Digital ID */}
            <Card className="overflow-hidden shadow-lg border-0 qr-glow">
              <CardContent className="p-0">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-xl shadow-md">
                      <QRCodeSVG
                        value={qrUrl}
                        size={80}
                        level="H"
                        includeMargin={false}
                      />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-white font-bold text-lg">Удостоверение</h2>
                      <p className="text-blue-200 text-xs mb-2">Предъявите клиенту</p>
                      <div className="flex items-center gap-1 text-green-300 text-xs font-bold bg-green-500/20 px-2 py-1 rounded-lg inline-flex">
                        <CheckCircle className="h-3 w-3" />
                        <span>Верифицирован</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] text-blue-300 text-center mt-3 tracking-widest uppercase">
                    Gasworkers Official ID
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-white shadow-sm border-slate-100">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">За сегодня</p>
                  <p className="text-xl font-bold text-slate-800">{formatPrice(todayEarnings.toString())}</p>
                  <p className="text-[10px] text-green-600 font-medium">
                    +{activeOrdersData?.data.filter((o) => o.status === 'COMPLETED' && new Date(o.completedAt || '') >= todayStart).length || 0} заказ
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-slate-100">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Баланс</p>
                  <p className="text-xl font-bold text-slate-800">{formatPrice(masterProfile.balance)}</p>
                  {Number(masterProfile.commissionDebt) > 0 && (
                    <p className="text-[10px] text-orange-500 font-medium">
                      Долг: {formatPrice(masterProfile.commissionDebt)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Active Order */}
            {activeOrder && (
              <Card className="bg-white shadow-md border-l-4 border-l-orange-500 overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Badge className={ORDER_STATUS_COLORS[activeOrder.status]}>
                      {ORDER_STATUS_LABELS[activeOrder.status]}
                    </Badge>
                    <span className="text-xs font-bold text-slate-800">#{activeOrder.orderNumber}</span>
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">{activeOrder.description}</h3>
                  {activeOrder.client && (
                    <p className="text-xs text-slate-500 mb-3">
                      <User className="inline h-3 w-3 mr-1" />
                      {activeOrder.client.name}
                    </p>
                  )}
                  <div className="flex gap-2">
                    {activeOrder.client?.phone && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => window.open(`tel:${activeOrder.client!.phone}`)}
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Позвонить
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      onClick={() => handleTabChange('orders')}
                    >
                      <ChevronRight className="h-4 w-4 mr-1" />
                      Подробнее
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Info */}
            <Card className="bg-white shadow-sm border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <Wrench className="h-4 w-4" />
                  <span className="text-xs font-medium">Ваши бренды</span>
                </div>
                <p className="text-sm font-medium text-slate-700">
                  {masterProfile.brands.length > 0
                    ? masterProfile.brands.slice(0, 4).join(', ')
                    : 'Все бренды'}
                  {masterProfile.brands.length > 4 && ` +${masterProfile.brands.length - 4}`}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-3">
            <h2 className="font-bold text-lg text-slate-700">Мои заказы</h2>

            {ordersLoading ? (
              <div className="py-8 text-center text-slate-500">Загрузка...</div>
            ) : allOrdersData?.data.length === 0 ? (
              <div className="py-8 text-center text-slate-500">Нет активных заказов</div>
            ) : (
              allOrdersData?.data.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            <h2 className="font-bold text-lg text-slate-700">История заказов</h2>

            {historyLoading ? (
              <div className="py-8 text-center text-slate-500">Загрузка...</div>
            ) : historyData?.data.length === 0 ? (
              <div className="py-8 text-center text-slate-500">Нет завершённых заказов</div>
            ) : (
              historyData?.data.map((order) => (
                <OrderCard key={order.id} order={order} compact />
              ))
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Profile Header */}
            <Card className="bg-white shadow-sm border-slate-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 mb-4">
                  {masterProfile.photoUrl ? (
                    <img
                      src={masterProfile.photoUrl}
                      alt={masterProfile.name}
                      className="w-16 h-16 rounded-full border-2 border-blue-100 object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-600">
                      {masterProfile.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{masterProfile.name}</h2>
                    <p className="text-sm text-slate-500">{masterProfile.masterType || 'Мастер'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      <span className="text-xl font-bold">{masterProfile.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                    <p className="text-[10px] text-slate-500">Рейтинг</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xl font-bold text-slate-800">{masterProfile.reviewCount}</p>
                    <p className="text-[10px] text-slate-500">Отзывов</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Shield className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-[10px] text-slate-500">Верифицирован</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Reviews */}
            <div>
              <h3 className="font-bold text-slate-700 mb-3">Отзывы</h3>
              {reviewsLoading ? (
                <div className="py-4 text-center text-slate-500">Загрузка...</div>
              ) : reviewsData?.data.length === 0 ? (
                <Card className="bg-white shadow-sm border-slate-100">
                  <CardContent className="p-6 text-center text-slate-500">
                    Пока нет отзывов
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {reviewsData?.data.map((review) => (
                    <Card key={review.id} className="bg-white shadow-sm border-slate-100">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-700 text-sm">{review.clientName}</span>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={cn(
                                  'h-3 w-3',
                                  i < review.rating
                                    ? 'text-yellow-500 fill-yellow-500'
                                    : 'text-slate-200'
                                )}
                              />
                            ))}
                          </div>
                        </div>
                        {review.comment && (
                          <p className="text-sm text-slate-600">{review.comment}</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-2">
                          {formatRelativeTime(review.createdAt)}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Balance Info */}
            <div>
              <h3 className="font-bold text-slate-700 mb-3">Финансы</h3>
              <Card className="bg-white shadow-sm border-slate-100">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-slate-600">Баланс</span>
                    <span className="text-xl font-bold text-green-600">{formatPrice(masterProfile.balance)}</span>
                  </div>
                  {Number(masterProfile.commissionDebt) > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">Долг по комиссии</span>
                      <span className="font-medium text-orange-500">{formatPrice(masterProfile.commissionDebt)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="safe-area-bottom">
          <div className="flex justify-around items-center h-16 max-w-md mx-auto px-2">
            {navItems.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => handleTabChange(id)}
                className={cn(
                  'flex flex-col items-center gap-1 w-16 py-2 rounded-xl transition-all',
                  activeTab === id ? 'text-blue-600' : 'text-slate-400'
                )}
              >
                <Icon className={cn('h-6 w-6', activeTab === id && 'transform -translate-y-0.5')} />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
}

// Order Card Component
function OrderCard({ order, compact = false }: { order: UserOrder; compact?: boolean }) {
  return (
    <Card className="bg-white shadow-sm border-slate-100 overflow-hidden">
      <CardContent className={cn('p-4', compact && 'py-3')}>
        <div className="flex gap-3">
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
              order.type === 'VIDEO_CONSULT' ? 'bg-blue-50 text-blue-600' :
              order.type === 'REPAIR' ? 'bg-orange-50 text-orange-600' :
              'bg-slate-50 text-slate-600'
            )}
          >
            {ORDER_TYPE_ICONS[order.type]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-slate-800 text-sm">#{order.orderNumber}</p>
                <p className="text-xs text-slate-500 line-clamp-1">{order.description}</p>
              </div>
              {order.finalPrice && (
                <span className="font-bold text-green-600 text-sm shrink-0 ml-2">
                  {formatPrice(order.finalPrice)}
                </span>
              )}
            </div>
            {!compact && order.client && (
              <p className="text-xs text-slate-400 mt-1">
                <User className="inline h-3 w-3 mr-1" />
                {order.client.name}
              </p>
            )}
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-slate-50 flex justify-between items-center">
          <span className="text-[10px] text-slate-400">{formatRelativeTime(order.createdAt)}</span>
          <Badge variant="outline" className={ORDER_STATUS_COLORS[order.status]}>
            {ORDER_STATUS_LABELS[order.status]}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

export default MasterProfilePage;
